/**
 * Click Engine - estado agregado de cliques (clickCount, rageClick, element fingerprint).
 * Não envia eventos; apenas atualiza estado e notifica o aggregator.
 */

import type { ClickState } from './types';

const RAGE_CLICK_COUNT = 3;
const RAGE_CLICK_WINDOW_MS = 500;

let state: ClickState = {
  clickCount: 0,
  rageClickCount: 0,
  lastClickAt: 0,
};

let clickListener: ((e: MouseEvent) => void) | null = null;
let onStateChange: ((s: ClickState) => void) | null = null;
let onPageClickEvent: ((s: ClickState, event: MouseEvent) => void) | null =
  null;

/** Histórico de cliques por fingerprint para detectar rage click. */
const lastClicksByFingerprint = new Map<string, number[]>();

let rageClickWindowMs = RAGE_CLICK_WINDOW_MS;

function notify(): void {
  if (onStateChange) onStateChange({ ...state });
}

/**
 * Gera fingerprint estável do elemento (tag + id + classes limitadas + data-* relevantes).
 */
export function getElementFingerprint(el: EventTarget | null): string {
  if (!el || !(el instanceof Element)) return 'unknown';
  const e = el as Element;
  const tag = e.tagName.toLowerCase();
  const id = e.id ? `#${e.id}` : '';
  const classes =
    e.className && typeof e.className === 'string'
      ? '.' + e.className.trim().split(/\s+/).slice(0, 3).join('.')
      : '';
  const dataAttrs: string[] = [];
  if (typeof e.getAttributeNames === 'function') {
    const data = e.getAttributeNames();
    data
      .filter(a => a.startsWith('data-'))
      .slice(0, 5)
      .sort()
      .forEach(a => {
        const v = e.getAttribute(a);
        if (v != null) dataAttrs.push(`${a}=${v.slice(0, 50)}`);
      });
  }
  return [tag, id, classes, dataAttrs.join('|')].filter(Boolean).join('');
}

function handleClick(e: MouseEvent): void {
  const now = Date.now();
  state.clickCount += 1;
  state.lastClickAt = now;

  const fp = getElementFingerprint(e.target);
  const list = lastClicksByFingerprint.get(fp) ?? [];
  list.push(now);
  const cutoff = now - rageClickWindowMs;
  const recent = list.filter(t => t >= cutoff);
  lastClicksByFingerprint.set(fp, recent);

  if (recent.length >= RAGE_CLICK_COUNT) {
    state.rageClickCount += 1;
    lastClicksByFingerprint.set(fp, []);
  }

  notify();
  if (onPageClickEvent) onPageClickEvent({ ...state }, e);
}

export function initClickBehavior(
  callback?: (s: ClickState) => void,
  options?: {
    rageClickWindowMs?: number;
    onPageClickEvent?: (s: ClickState, event: MouseEvent) => void;
  }
): void {
  if (typeof window === 'undefined') return;
  onStateChange = callback ?? null;
  onPageClickEvent = options?.onPageClickEvent ?? null;
  rageClickWindowMs = options?.rageClickWindowMs ?? RAGE_CLICK_WINDOW_MS;
  state = {
    clickCount: 0,
    rageClickCount: 0,
    lastClickAt: 0,
  };
  lastClicksByFingerprint.clear();

  if (clickListener) {
    window.removeEventListener('click', clickListener);
  }
  clickListener = handleClick;
  window.addEventListener('click', clickListener);
  notify();
}

export function getClickState(): ClickState {
  return { ...state };
}

export function resetClickState(): void {
  state = {
    clickCount: 0,
    rageClickCount: 0,
    lastClickAt: state.lastClickAt,
  };
  lastClicksByFingerprint.clear();
  notify();
}

export function destroyClickBehavior(): void {
  if (clickListener && typeof window !== 'undefined') {
    window.removeEventListener('click', clickListener);
  }
  clickListener = null;
  onStateChange = null;
  onPageClickEvent = null;
  lastClicksByFingerprint.clear();
}
